/**
 * Hook pour vérifier les mises à jour en background
 * Vérifie périodiquement si les données ont changé sur le serveur
 * Mise à jour silencieuse du cache si changements détectés
 * Utilise coverage pour charger uniquement les "trous" manquants
 */

import { useEffect, useRef } from 'react';
import { DateTime } from 'luxon';
import { cacheGet, cacheSet, emitCacheUpdated } from '../utils/cache';
import { API_ENDPOINTS, getAuthHeaders } from '../config/api';
import { readCoverage, writeCoverage, addSpan, findHoles, removeStaleSpans } from '../utils/cacheCoverage';
import { mergeAttendance } from '../utils/attendanceMerge';

// Constantes
const ACTIVITY_MAX_AGE_MS = 5 * 60 * 1000; // 5 min
const VISIBLE_WINDOW_DAYS = 7; // ±7 jours autour d'aujourd'hui
const TZ = 'Europe/Paris';

/**
 * Obtient la date d'aujourd'hui en ISO (YYYY-MM-DD) en timezone France
 */
function todayISO() {
  return DateTime.now().setZone(TZ).toISODate();
}

/**
 * Charge l'attendance pour une plage de dates
 * @param {string} from - Date de début (YYYY-MM-DD)
 * @param {string} to - Date de fin (YYYY-MM-DD)
 * @returns {Promise<Array>} Données attendance
 */
async function fetchAttendanceRange(from, to) {
  const userId = localStorage.getItem('userId') || localStorage.getItem('uid') || localStorage.getItem('user_id') || "";
  const res = await fetch(API_ENDPOINTS.ATTENDANCE_DAYS(from, to, userId), { 
    headers: getAuthHeaders(),
    credentials: 'include'
  });
  
  if (!res.ok) {
    throw new Error(`Failed to fetch attendance: ${res.status}`);
  }
  
  return res.json();
}

/**
 * Hook pour sync en background des données activity
 * Charge uniquement les "trous" manquants dans la coverage
 * Vérifie toutes les 2 minutes
 */
export function useBackgroundActivitySync() {
  const intervalRef = useRef(null);
  const isCheckingRef = useRef(false);

  useEffect(() => {
    const checkForUpdates = async () => {
      // Éviter les appels concurrents
      if (isCheckingRef.current) return;
      
      isCheckingRef.current = true;
      
      try {
        // Fenêtre par défaut : today-7 → today+7 (période visible typique)
        const today = todayISO();
        const from = DateTime.fromISO(today).minus({ days: VISIBLE_WINDOW_DAYS }).toISODate();
        const to = DateTime.fromISO(today).plus({ days: VISIBLE_WINDOW_DAYS }).toISODate();
        
        // Lire la coverage actuelle et purger les spans trop vieux
        let cov = readCoverage();
        cov = removeStaleSpans(cov, ACTIVITY_MAX_AGE_MS);
        
        // Calculer les trous à combler sur la fenêtre
        const holes = findHoles(cov, from, to);
        
        if (holes.length === 0) {
          console.log('✅ [backgroundSync] Aucun trou à combler, coverage complète');
          isCheckingRef.current = false;
          return;
        }
        
        console.log(`🔄 [backgroundSync] ${holes.length} trou(s) détecté(s), chargement...`);
        
        // Charger chaque trou et merger progressivement
        let mergedAll = cacheGet('activity/attendance', []);
        
        for (const hole of holes) {
          console.log(`📥 [backgroundSync] Chargement trou: ${hole.from} → ${hole.to}`);
          const rows = await fetchAttendanceRange(hole.from, hole.to);
          
          // Merge avec les données existantes
          mergedAll = mergeAttendance(mergedAll, rows);
          
          // Barrière anti-écrasement: relire le cache et re-merger
          // au cas où une autre opération aurait mis à jour pendant notre fetch
          const current = cacheGet('activity/attendance', []);
          mergedAll = mergeAttendance(current, mergedAll);
          
          // Écrire dans le cache
          cacheSet('activity/attendance', mergedAll);
          emitCacheUpdated('activity/attendance');
          
          // Mettre à jour la coverage
          cov = addSpan(cov, { from: hole.from, to: hole.to, fetchedAt: Date.now() });
          
          console.log(`✅ [backgroundSync] Trou comblé: ${hole.from} → ${hole.to} (${rows.length} lignes)`);
        }
        
        // Persister la nouvelle coverage
        writeCoverage(cov);
        console.log('✅ [backgroundSync] Coverage mise à jour');
        
      } catch (err) {
        console.warn('[backgroundSync] Erreur lors de la sync:', err);
      } finally {
        isCheckingRef.current = false;
      }
    };
    
    // Première vérification après 30s (laisser le temps de se stabiliser)
    const initialTimeout = setTimeout(() => {
      checkForUpdates();
    }, 30000);
    
    // Puis vérification toutes les 2 minutes
    intervalRef.current = setInterval(() => {
      checkForUpdates();
    }, 2 * 60 * 1000);
    
    return () => {
      clearTimeout(initialTimeout);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);
}
